import { useState } from 'react'
import { useSelector } from 'react-redux'

import InviteYourTeam from './components/InviteYourTeam/InviteYourTeam'

import styles from './styles.module.scss'

import wisprLogo from '@/assets/images/wisprLogo.svg?url'
import Button from '@/components/Button'
import Checkbox from '@/components/Checkbox'
import Input from '@/components/Input'
import { Spinner } from '@/components/Spinner'
import { selectIsDomainLoading } from '@/store/features/domain/domainSlice'
import { selectSubscription } from '@/store/features/user/userSlice'
interface CreateTeamProps {
  loading: boolean
  orgName: string
  setOrgName: (orgName: string) => void
  orgDomain?: string
  onSubmit: (emails: string[], autoInviteUsers: boolean) => void
  salesAssistanceError?: boolean
}

export default function CreateTeam(props: CreateTeamProps) {
  const { loading, orgName, setOrgName, orgDomain, onSubmit, salesAssistanceError } = props
  const isDomainLoading = useSelector(selectIsDomainLoading)
  const subscription = useSelector(selectSubscription)

  // We'll hold onto the emails submitted from the InviteYourTeam component
  // Having at least one indicates completion if the first step
  const [emails, setEmails] = useState<string[]>([])
  // Step 0 is emails, step 1 is team name
  const [step, setStep] = useState<0 | 1>(0)

  const handleSubmitEmails = (emails: string[]) => {
    setEmails(emails)
    setStep(1)
  }

  const isTeamTrialUnavailable = subscription?.team_trial_given === true

  const title = step === 0 ? 'Invite your team to join you on Flow' : "Let's name your team"
  const description =
    step === 0 ? 'Unlock shared snippets, dictionary, unified billing, admin controls and more' : undefined

  return (
    <div className={styles.createOrgContainer}>
      {isTeamTrialUnavailable && (
        <div className={styles.warningBanner}>
          <span>
            You&rsquo;ve already used your team trial. You have 24 hours to add billing information to your new team
            before it gets disbanded.
          </span>
        </div>
      )}
      <img src={wisprLogo} alt="Wispr" className={styles.logo} />
      {salesAssistanceError && (
        <div className={styles.warningContainer}>
          <span>
            Please reach out to your sales or customer success representative to sign up. If you don&rsquo;t have one,
            please reach out to us:{' '}
            <a href="https://wisprflow.ai/talk-to-sales" rel="noopener noreferrer" target="_blank">
              https://wisprflow.ai/talk-to-sales
            </a>
            .
          </span>
        </div>
      )}
      <h5>{title}</h5>
      {description && <div className={styles.information}>{description}</div>}
      {loading || isDomainLoading ? (
        <Spinner size={40} color="black" />
      ) : step === 0 ? (
        <InviteYourTeam onSubmit={handleSubmitEmails} initialEmails={emails} />
      ) : (
        <TeamNameForm
          loading={loading}
          orgName={orgName}
          setOrgName={setOrgName}
          orgDomain={orgDomain}
          onSubmit={(autoInviteUsers) => onSubmit(emails, autoInviteUsers)}
          onBack={() => setStep(0)}
        />
      )}
      <div className={styles.disclaimer}>
        By creating an organization, you agree to our{' '}
        <a href="https://www.wisprflow.ai/terms-of-service">Terms of Service</a> and{' '}
        <a href="https://www.wisprflow.ai/privacy-policy">Privacy Policy</a>.
      </div>
    </div>
  )
}
interface TeamNameFormProps {
  loading: boolean
  orgName: string
  setOrgName: (orgName: string) => void
  orgDomain?: string
  onSubmit: (autoInviteUsers: boolean) => void
  onBack?: () => void
}

const TeamNameForm = (props: TeamNameFormProps) => {
  const { loading, orgName, setOrgName, orgDomain, onSubmit, onBack } = props
  const [autoInviteUsers, setAutoInviteUsers] = useState(true)

  return (
    <div className={styles.createOrgForm}>
      <div className={styles.createOrgFormRow}>
        <Input type="text" value={orgName} onInput={setOrgName} placeholder="Team name" isDisabled={loading} />
      </div>
      {orgDomain && (
        <>
          <div className={styles.createOrgFormDomain}>Domain: {orgDomain}</div>
          <div className={styles.createOrgFormRow}>
            <Checkbox
              checked={autoInviteUsers}
              onChange={() => setAutoInviteUsers(!autoInviteUsers)}
              label={`Automatically add new users with a ${orgDomain} email to my team`}
            />
          </div>
        </>
      )}
      <div className={styles.createOrgFormRow}>
        <Button type="light" isDisabled={loading} title="Back" onClick={() => onBack?.()} />
        <Button
          type="dark"
          isDisabled={loading || !orgName.trim()}
          title="Create"
          onClick={() => onSubmit(autoInviteUsers)}
        />
      </div>
    </div>
  )
}
